import { useEffect, useState } from 'react';

import {
  httpGetPlaylistsByPlaylistId,
  httpGetPortalsByPortalId,
} from '@iconik-io/iconik-api/src/services/assets';

import { createEntityDenormalizer } from '../../../redux/entities/normalizers';
import { useAppDispatch, useAppSelector } from '../../../redux/redux_hooks';

const denormalizePlaylist = createEntityDenormalizer('playlists');

export const usePortalWithPlaylist = (portalId: string | undefined) => {
  const dispatch = useAppDispatch();
  const portal = useAppSelector((state) =>
    portalId ? state.entities.portals[portalId] : undefined,
  );
  const playlist = useAppSelector((state) =>
    portal?.playlist_id
      ? denormalizePlaylist(state.entities, portal.playlist_id)
      : undefined,
  );
  const [isLoading, setIsLoading] = useState(() => !(portal && playlist));

  // biome-ignore lint/correctness/useExhaustiveDependencies: portal/playlist are read on portalId change to decide whether the cache covers the new id; we don't want to re-run when fetch updates them.
  useEffect(() => {
    if (!portalId) {
      setIsLoading(false);
      return;
    }
    if (portal && playlist) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    dispatch(httpGetPortalsByPortalId(portalId))
      .then((response) => {
        if (cancelled) {
          return;
        }
        const playlistId = response.body?.playlist_id;
        if (playlistId) {
          return dispatch(httpGetPlaylistsByPlaylistId(playlistId));
        }
      })
      .catch(() => {
        // Swallow: an error leaves `portal` unset, so the not-found state
        // renders instead of an unhandled rejection.
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [portalId]);

  return { portal, playlist, isLoading };
};
